import { motion } from 'framer-motion'
import { Howl } from 'howler'
import { uniqueId } from 'lodash'
import React from 'react'
import AudioPlayer, { AudioPlayerVariant, variantClassMap } from './AudioPlayer'
import StatusMessage from './StatusMessage'
import EllipsisAnimation from '@/components/animations/EllipsisAnimation'
import { Row, Show, Text } from '@/components/core'
import { Match, Switch } from '@/components/switch'
import { useTimer } from '@/hooks/useTimer.ts'

interface UserMessageProps {
  message?: string
  audioUrl?: string
  audioBlob?: Blob
  variant?: AudioPlayerVariant
  pending?: boolean
  showControls?: boolean
  recordingFeedback?: boolean
  statusFeedback?: boolean
}

const UserMessage = ({
  message,
  audioUrl,
  audioBlob,
  variant = 'tertiary',
  pending,
  recordingFeedback,
  showControls,
  statusFeedback = true
}: UserMessageProps) => {
  const { currentTime, start } = useTimer({
    endTime: 2000,
    autoStart: false
  })

  const [loaded, setLoaded] = React.useState(false)

  const audioRef = React.useRef<HTMLMediaElement | null>(null)
  const userAudioRef = React.useRef<Howl | null>(null)
  const effectAudioRef = React.useRef<Howl | null>(null)

  const completedAudioUrl: Promise<string | undefined> = React.useMemo(() => {
    if (audioBlob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(audioBlob)
      })
    }
    if (audioUrl) {
      return Promise.resolve(
        `${import.meta.env.VITE_RECORD_BASE_URL}/${audioUrl}`
      )
    }

    return Promise.resolve(undefined)
  }, [audioUrl, audioBlob])

  React.useEffect(() => {
    ;(async () => {
      const url = await completedAudioUrl
      if (!audioRef.current && url) {
        userAudioRef.current = new Howl({
          src: [url],
          html5: true,
          autoplay: false,
          onload: () => {
            //@ts-expect-error the variable is not mapped by ts
            audioRef.current = userAudioRef.current._sounds[0]._node
            setLoaded(true)
          }
        })
      }
    })()
  }, [audioUrl, audioBlob])

  React.useEffect(() => {
    if (recordingFeedback && !effectAudioRef.current) {
      effectAudioRef.current = new Howl({
        src: ['/audio/user-message.mp3'],
        html5: true,
        autoplay: true,
        onloaderror: (_, error) => console.log(error),
        onplayerror: (_, error) => console.log(error)
      })
    }

    return () => {
      if (effectAudioRef.current) {
        effectAudioRef.current.unload()
      }
      if (userAudioRef.current) {
        userAudioRef.current.unload()
      }
      if (audioRef.current) {
        audioRef.current.remove()
      }
    }
  }, [])

  React.useEffect(() => {
    if (pending) {
      start()
    }
  }, [pending, start])

  const layoutId = uniqueId('user-message')

  const pendingState = pending || !loaded || (!audioUrl && !audioBlob)
  const pendingPlaceholder = (
    <motion.div
      layout
      layoutId={layoutId}
      className={`${variantClassMap[variant]?.backgroundColor} flex h-14 w-full max-w-[238px] flex-row items-center space-x-8 rounded-2xl rounded-tr-none px-5 py-3`}
    >
      <Text className="w-full text-white/80">
        <Row className="items-center">
          <Text.Translated
            size="xs"
            fontWeight="medium"
            translationKey="loading_user_message"
            className="text-white/80"
          />
          <EllipsisAnimation />
        </Row>
      </Text>
      <Show when={statusFeedback}>
        <StatusMessage
          status={currentTime < 800 ? 0 : currentTime < 2000 ? 1 : 2}
        />
      </Show>
    </motion.div>
  )
  return (
    <>
      <Show when={!pendingState || !!message} fallback={pendingPlaceholder}>
        <motion.div
          layout
          layoutId={layoutId}
          className={`${variantClassMap[variant]?.backgroundColor} flex h-14 w-full max-w-[358px] flex-row items-center space-x-8 rounded-2xl rounded-tr-none px-5 py-3`}
        >
          <Switch>
            <Match when={!!audioRef.current}>
              <motion.div
                layout
                layoutId={layoutId}
                className="flex w-full flex-row"
              >
                <AudioPlayer
                  audioMedia={audioRef.current}
                  variant={variant}
                  showControls={showControls}
                  containerClassName="max-h-12"
                />
              </motion.div>
            </Match>
            <Match when={!!message}>
              <Text
                size="xs"
                fontWeight="medium"
                className="w-full px-4 text-white/80"
              >
                {message}
              </Text>
            </Match>
          </Switch>
          <Show when={statusFeedback}>
            <StatusMessage
              status={currentTime < 800 ? 0 : currentTime < 2000 ? 1 : 2}
            />
          </Show>
        </motion.div>
      </Show>
    </>
  )
}

export default UserMessage
